import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Button, DropdownMenu, Dropdown, DropdownToggle, NavItem } from 'reactstrap';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Icon from 'shared/components/Icon';
import { RootState } from 'shared/store/types';
import { CustomerType, UserRole } from 'shared/models';
import { getIsNavOpen } from 'shared/store/modules/nav/selectors';
import { selectActiveAccount, selectNumberOfAccounts } from 'shared/store/modules/accounts/selectors';
import { selectActiveMaintenanceMessage } from 'shared/store/modules/systemMaintenanceMessage/selectors';
import { toggleNav } from 'shared/store/modules/nav/actions';

import styles from './Header.styles';
import { DisplaySystemMessage } from '../ApplicationLayout';

export function Header() {
  const account = useSelector(selectActiveAccount);
  const systemMaintenanceMessage = useSelector(selectActiveMaintenanceMessage) as string | null;
  const systemMaintenanceShowUntilTime = useSelector(
    (state: RootState) => state.systemMaintenanceShowUntilTime as string | null
  );

  const user = useSelector((state: RootState) => state.user) as { firstName: string; lastName: string; email?: string };
  const { firstName } = user;
  const dispatch = useDispatch();
  const isNavOpen = useSelector(getIsNavOpen);
  const numberOfAccounts = useSelector(selectNumberOfAccounts);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const setHeight = (target: HTMLElement) =>
      document.documentElement.style.setProperty('--header-height', `${target.offsetHeight}px`);
    if (typeof ResizeObserver === 'undefined') {
      setHeight(el);
      return;
    }
    const observer = new ResizeObserver(([entry]) => setHeight(entry.target as HTMLElement));
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--header-height');
    };
  }, []);

  const toggleDropdown = () => setDropdownOpen(prevState => !prevState);

  const handleDropdownItemClick = (e: React.MouseEvent) => {
    // Remove focus from the clicked element to prevent aria-hidden warning
    (e.currentTarget as HTMLElement).blur();

    setDropdownOpen(false);
    if (isNavOpen) {
      dispatch(toggleNav());
    }
  };

  const cls = classNames({
    nav: true,
    'nav-toggler': true,
    active: isNavOpen
  });

  const DisplayAccountName = ({
    name,
    customerType,
    customerName
  }: {
    name: String;
    customerType: number;
    customerName: String;
  }) => {
    const Name = () => {
      if (customerType !== CustomerType.Commercial && customerName === name) {
        return null;
      }
      return <p className="mb-0 mr-4 subscriber-name">{customerName}</p>;
    };

    return (
      <div>
        <span className="account-name">{name}</span>
        <Name />
      </div>
    );
  };

  return (
    <div ref={headerRef} css={styles}>
      <DisplaySystemMessage
        systemMaintenanceMessage={systemMaintenanceMessage}
        mobile={true}
        untilTime={systemMaintenanceShowUntilTime}
      />
      <header className="header">
        <nav className="nav-utility">
          <ul className={cls}>
            <NavItem>
              <Button
                className="account-toggle text-white"
                color="link"
                onClick={() => dispatch(toggleNav())}
                outline
                tag="span"
              >
                <Icon icon={isNavOpen ? 'chevron_left' : 'menu'} />
              </Button>
            </NavItem>
          </ul>

          <ul className="nav ml-auto">
            <Dropdown inNavbar nav isOpen={dropdownOpen} toggle={toggleDropdown}>
              <DropdownToggle className={`account-toggle${firstName && account.name ? '-double' : ''}`} nav>
                <div className="d-flex align-items-center">
                  <DisplayAccountName
                    name={firstName}
                    customerType={account.customerType}
                    customerName={account.name}
                  />
                  <Icon icon="account_circle" />
                </div>
              </DropdownToggle>

              <DropdownMenu className="ddm-account" end>
                <div className="account-email">{user?.email}</div>
                <Link className="dropdown-item" to="/profile" onClick={handleDropdownItemClick}>
                  My Profile
                </Link>

                {account.role === UserRole['Account Manager'] && (
                  <Link className="dropdown-item" to="/settings" onClick={handleDropdownItemClick}>
                    Account Settings
                  </Link>
                )}

                {numberOfAccounts > 1 && (
                  <Link className="dropdown-item" to="/accounts" onClick={handleDropdownItemClick}>
                    Change Account
                  </Link>
                )}

                <Link className="dropdown-item" to="/logout" onClick={handleDropdownItemClick}>
                  Sign out
                </Link>
              </DropdownMenu>
            </Dropdown>
          </ul>
        </nav>
      </header>
    </div>
  );
}
